import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {useMemo} from 'react'
import {Link, useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {StoreLogos} from '~/icons/store-logos'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {objectToQueryString} from '~/utils/commons'
import {grantAllConsent} from '~/utils/consent-manager.client'
import DiscountCornerRibbon from '../badges/discount-corner-ribbon'

interface ListingsCtaProps extends React.HTMLAttributes<HTMLDivElement> {
  product: Product
  index: number
  placement: string
  showProductPageLinks?: boolean
}
const ListingsCta: FunctionComponent<ListingsCtaProps> = ({
  product,
  index,
  placement,
  showProductPageLinks = true,
  ...attributes
}: ListingsCtaProps) => {
  const {language, currencyCode, locale, featureFlags, trackAnalytics, domain, deviceType} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const {
    clickId,
    setShowCookieConsentGdpr,
    dataResponse: {productsCategory} = {} as SlugApiResponse,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const {styles} = getDomainConfig(domain, locale)
  const submit = useSubmit()

  const {productStorePageSlug, storeListings, name, slug} = product
  const {partner, discount, price, affiliateLink, productId, freeShipping} = storeListings?.amazon ?? {}

  const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)
  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId ?? '')}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'buying-options',
    widget_place: `${placement}-${index}`,
  })

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
      }),
    [language, currencyCode],
  )
  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportClick({
      widget_name: 'buying-options',
      widget_place: `${placement}-card`,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl ?? '')
    }
  }

  return (
    <div ref={widgetRef} className="flex w-full flex-col items-center justify-center gap-y-2 tablet:my-2">
      <div {...attributes} className="relative w-full">
        <div
          key={`${productId}-${index}`}
          className={clsx(
            'flex min-h-[85px] min-w-full flex-row items-center justify-between gap-4 rounded-xl border bg-white p-3 desktop:p-2 tablet:flex-col',
            styles.CARD_BORDER,
          )}
        >
          <a
            className="absolute inset-0"
            href={redirectUrl}
            onClick={handleCardClick}
            aria-label={`view ${name}`}
            target="_blank"
            rel="sponsored noreferrer"
          ></a>
          {discount && discount > 0 ? (
            <DiscountCornerRibbon
              className="absolute -left-[1px] -top-[1px] [&>div>.ribbon]:size-14"
              content={translations.Badge.discountOff(discount)}
            />
          ) : null}

          <div className="grid w-full grid-cols-3 items-center gap-y-3 desktop:grid-cols-2 tablet:mt-1">
            <div
              className={clsx(
                'flex flex-col items-center justify-center gap-1 text-xs leading-3 text-gray-500 desktop:col-span-2 desktop:gap-0.5 desktop:text-[7px]',
              )}
            >
              {translations.ProductPage.availableOn}
              <StoreLogos partner={partner} className="desktop:h-4 desktop:w-12" />
            </div>

            <div className="hidden h-fit items-center justify-center text-center text-xs font-light text-gray-500 desktop:col-span-1 tablet:flex">
              {freeShipping ? translations.Badge.freeShipping : null}
            </div>
            <div className="flex h-fit w-fit flex-col items-center justify-between desktop:col-span-1">
              {price ? (
                <div className="h-fit text-[23px] font-medium leading-5 text-gray-900">
                  {currencyFormatter.format(price)}
                </div>
              ) : null}
              {discount && discount > 0 ? (
                <div className="mb-2 mt-1 h-3 text-xs text-red-600">
                  {translations.ProductPage.discountCard(discount)}
                </div>
              ) : null}
            </div>
            <div
              className={clsx(
                'my-auto flex h-7 min-w-24 items-center justify-center rounded-full px-2 text-base font-normal desktop:col-span-1 tablet:col-span-3 tablet:h-11 tablet:w-full tablet:min-w-full',
                styles.LISTPAGE_BUTTON_PRIMARY,
                'listpage_button_primary',
              )}
            >
              {deviceType === 'desktop' ? translations.ProductCard.view : translations.ProductCard.viewProduct}
            </div>
          </div>
        </div>
      </div>
      {showProductPageLinks ? (
        <Link
          to={`/pp/${slug}/${productStorePageSlug}`}
          prefetch="intent"
          aria-label={translations.ProductCard.readFullReview}
          onClick={() => {
            reportClick({
              widget_name: 'read-full-review-store-page-link',
              widget_place: `card-${index}`,
            })
          }}
          className={clsx(
            'relative flex flex-row items-center gap-2 text-sm font-medium tablet:text-sm',
            styles.LISTPAGE_BUTTON_LINK,
            'listpage_button_link',
          )}
        >
          {translations.ProductCard.viewOptions(deviceType === 'desktop')}
          <IconChevronRightThick
            className={clsx('size-4 tablet:rotate-90', styles.LISTPAGE_BUTTON_ICON_FILL, 'listpage_button_icon_fill')}
          />
        </Link>
      ) : null}
    </div>
  )
}

export default ListingsCta
